import React from 'react'
import { useInspectorContext, useUserColorFormat } from '../../hooks/inspector'
import {
  SketchFrameElement,
  SketchLayerElement,
  SketchElement,
  isPageElement,
  ElementType,
  isLayerWithSharedStyle,
  isLayerElement,
  isFrameElement,
  SketchLayerCSSProperties,
} from '../../../../inspector'

import {
  PositionAndSize,
  Overview,
  InspectorSkeleton,
  ArtboardOverview,
  Corners,
  Appearance,
  Text,
  TextContent,
  Style,
  TextOrLayerStyles,
  SymbolInSidebar,
  Exports,
  Stack,
} from './sections'
import { SidebarEmpty, useModalContext } from '@sketch/components'
import { CollapsibleInspectorSection } from './components'

import * as S from './FrameDetailInspector.styles'
import {
  useEditableComponentDescription,
  // useSetupComponentDescriptions,
} from './useEditableComponentDescription'
import { EditableComponentDescription } from '../../../ComponentsWebView/components/ComponentDescription'
import { ShareWithoutVersion } from '../../../../versioning'
import { VersionFragment } from '@sketch/gql-types'
import { useComponentsTrigger } from '../../../components/ComponentsStateContext'

import { ReactComponent as CodeIcon } from '@sketch/icons/code-block-64'
import { Layout } from './sections/Layout'
import { Code } from './sections/Code'
import { ReactComponent as RectanglesIcon } from '@sketch/icons/rectangles-2-17'
import { ReactComponent as Arrow4CornersIcon } from '@sketch/icons/arrow-4-corners-17'
import copy from 'copy-to-clipboard'
import { CodePreviewModal } from './components/CodePreviewModal'
import { useGenerateLayerCSS } from './useGenerateLayerCSS'

type FrameDetailInspectorProps = {
  share?: ShareWithoutVersion
  currentVersion?: VersionFragment | undefined
}

export function FrameDetailInspector({
  share,
  currentVersion,
}: FrameDetailInspectorProps) {
  const { selectedElement, inspectorStatus } = useInspectorContext()

  // @TODO: This cannot be made conditional at the moment, and we won't always have a `share`
  // because inspector can be used for previews now. We'll need to revisit this if we choose
  // to support editable component descriptions/component tagging in the web app.
  //
  // Start fetching before isInspectorReady is true
  // useSetupComponentDescriptions(
  //   share.identifier,
  //   selectedElement,
  //   currentVersion
  // )

  // Trigger async components ingestion
  useComponentsTrigger()

  if (inspectorStatus === 'pending') {
    return <InspectorSkeleton />
  }

  if (inspectorStatus === 'errored') {
    return (
      <S.Container>
        <SidebarEmpty
          title="Inspector failed to load"
          description={
            <>
              We’re looking into the issue. Try again later or visit our{' '}
              <a href="https://status.sketch.com/">Status Page</a>.
            </>
          }
        />
      </S.Container>
    )
  }

  if (!selectedElement) {
    return (
      <S.Container>
        <SidebarEmpty
          title="Select a layer to inspect it"
          icon={CodeIcon}
          description={
            <>
              Right-click or Control-click on any layer, then select it in the
              menu.
              <S.EmptyStateSeparator />
              Toggle Grid{' '}
              <S.EmptyStateShortCutPill>Shift G</S.EmptyStateShortCutPill> or
              Layout{' '}
              <S.EmptyStateShortCutPill>Shift L</S.EmptyStateShortCutPill>
            </>
          }
        />
      </S.Container>
    )
  }

  if (isPageElement(selectedElement)) {
    throw new Error('Page element not yet supported in inspector')
  }

  return (
    <S.Container>
      <Overview sketchElement={selectedElement} />
      {isFrameElement(selectedElement) && (
        <ArtboardElementInfo artboardElement={selectedElement} />
      )}
      <ElementInfo
        currentVersion={currentVersion}
        element={selectedElement}
        shareIdentifier={share?.identifier}
      />
    </S.Container>
  )
}

type ArtboardElementInfoProps = {
  artboardElement: SketchFrameElement
}

function ArtboardElementInfo({ artboardElement }: ArtboardElementInfoProps) {
  const { description, isEnabled, editDescriptionHandler } =
    useEditableComponentDescription()

  return (
    <>
      {artboardElement.type === ElementType.SymbolMaster && (
        <EditableComponentDescription
          description={description}
          disabled={!isEnabled}
          onEdit={editDescriptionHandler}
        />
      )}

      {!artboardElement.frameAncestorElement && 'preset' in artboardElement && (
        <ArtboardOverview
          preset={artboardElement.preset}
          frame={artboardElement.frameRelativeBoxRect}
        />
      )}
    </>
  )
}

type ElementInfoProps = {
  element: SketchLayerElement | SketchFrameElement
  currentVersion: VersionFragment | undefined
  shareIdentifier?: string
}

function ElementInfo({
  element,
  currentVersion,
  shareIdentifier,
}: ElementInfoProps) {
  const { showModal } = useModalContext()
  const { hasAnyLayerWithExportableAssets } = useInspectorContext()
  const [colorFormat, setColorFormat] = useUserColorFormat()

  const isDirty =
    element.style && 'isDirty' in element.style ? element.style?.isDirty : false

  const textOrLayerStyle = isLayerWithSharedStyle(element.style)
    ? element.style.sharedStyleReference
    : undefined

  const hasSymbols =
    isLayerElement(element) &&
    (element.type === ElementType.SymbolInstance ||
      !!element.parentSymbolLayerElement)

  const layerCSS = useGenerateLayerCSS(element as SketchLayerCSSProperties)

  const handleCodePreviewModal = () => {
    if (!element.style) return

    showModal(CodePreviewModal, {
      element: element,
    })
  }

  const handleCopyCSSClick = () => {
    if (!layerCSS.css.length) return
    copy(layerCSS.toString())
  }

  return (
    <>
      <PositionAndSize
        key={`${element.identifier.toString()}-position-and-size`}
        x={element.frameRelativeBoxRect.x}
        y={element.frameRelativeBoxRect.y}
        width={element.layerSize.width}
        widthBehaviour={element.layerSize.widthBehaviour}
        height={element.layerSize.height}
        heightBehaviour={element.layerSize.heightBehaviour}
        rotate={isLayerElement(element) ? element.rotation : undefined}
        element={element}
      />

      {element.type === ElementType.Artboard && <Layout element={element} />}

      {(element.type === ElementType.Artboard ||
        element.type === ElementType.SymbolMaster) &&
        element.stack && <Stack stack={{ ...element.stack }} />}

      {element.style && (
        <CollapsibleInspectorSection
          key={`${element.identifier.toString()}-css`}
          title="CSS"
          noPadding
          data-testid="inspector-sidebar-css"
          noMarginBottom
          actions={[
            {
              icon: <Arrow4CornersIcon width={17} height={17} />,
              onClick: handleCodePreviewModal,
            },
            {
              icon: <RectanglesIcon width={17} height={17} />,
              tooltipText: 'Copied',
              onClick: handleCopyCSSClick,
            },
          ]}
        >
          <Code styles={layerCSS.css} />
        </CollapsibleInspectorSection>
      )}

      {/* Text or Layer styles */}
      {shareIdentifier && textOrLayerStyle && currentVersion && (
        <TextOrLayerStyles
          isDirty={isDirty}
          isTextStyleComponent={element.type === ElementType.Text}
          shareIdentifier={shareIdentifier}
          currentVersion={currentVersion}
          sharedStyleReference={textOrLayerStyle}
        />
      )}

      {isLayerElement(element) && (
        <>
          {element.appearance && (
            <Appearance
              key={`${element.identifier.toString()}-appearance`}
              appearance={element.appearance}
            />
          )}
          {element.corners && (
            <Corners
              key={`${element.identifier.toString()}-corners`}
              corners={element.corners}
            />
          )}
        </>
      )}

      <TextSection sketchElement={element} />

      {element.style && (
        <Style
          key={`${element.identifier.toString()}-style`}
          style={element.style}
          onColorFormatChange={setColorFormat}
          colorFormat={colorFormat}
          layerType={element.type}
          glassProperties={element.prMarinaNode.getGlassProperties()}
        />
      )}
      {isLayerElement(element) && element.text && (
        <TextContent key={`${element.identifier.toString()}}-text-content`}>
          {element.text.string}
        </TextContent>
      )}

      {/* Symbols */}
      {hasSymbols && <SymbolInSidebar layerElement={element} />}

      {currentVersion && (
        <S.ExportsWrapper>
          <Exports
            key={`${element.identifier.toString()}-export`}
            exportType="layer"
            exportFormats={element.exportFormats}
            hasOtherExportableLayers={hasAnyLayerWithExportableAssets}
            currentVersion={currentVersion}
            exportableAssetId={element.exportId}
            nodeIdentifier={element.identifier}
            elementName={element.name}
          />
        </S.ExportsWrapper>
      )}
    </>
  )
}

type TextSectionProps = {
  sketchElement: SketchElement
}

function TextSection({ sketchElement }: TextSectionProps) {
  const [colorFormat, setColorFormat] = useUserColorFormat()

  if (sketchElement.type !== ElementType.Text || !sketchElement.text) {
    return null
  }

  const style = sketchElement.style

  return (
    <Text
      attributedString={sketchElement.text}
      verticalAlignment={style.verticalAlignment}
      originalSharedStyleValues={style.originalSharedStyleValues}
      onColorFormatChange={setColorFormat}
      colorFormat={colorFormat}
    />
  )
}
